import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { getProductImageUrl } from '../../../utils/productImage';
import { hasDisplayPrice } from '../../../utils/priceDisplay';

const ProductPortfolioSection = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('endoscopy');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const categories = [
    { 
      id: 'endoscopy', 
      name: 'Endoscopy', 
      icon: 'Eye',
      description: 'Endoscopic procedures and accessories',
      productCount: 25
    },
    { 
      id: 'surgery', 
      name: 'Surgery', 
      icon: 'Scissors',
      description: 'Surgical instruments and procedures',
      productCount: 42
    }
  ];

  const endoscopyProducts = [
    {
      id: 1,
      name: "Papillotome/Sphincterotome",
      category: "endoscopy",
      image: "/assets/images/papillotome-sphincterotome.jpg",
      description: "Precision papillotome and sphincterotome for ERCP procedures.",
      features: ["Precision cutting", "ERCP procedures", "Professional grade"],
      isNew: false,
      isFeatured: true
    },
    {
      id: 2,
      name: "PEG Kit",
      category: "endoscopy",
      image: "/assets/images/peg-kit.jpg",
      description: "Complete PEG (Percutaneous Endoscopic Gastrostomy) kit system.",
      features: ["Complete kit", "Sterile packaging", "Easy insertion"],
      isNew: false,
      isFeatured: true
    },
    {
      id: 3,
      name: "Hemoclip",
      category: "endoscopy",
      image: "/assets/images/hemoclip.jpg",
      description: "Hemostatic clips for bleeding control during procedures.",
      features: ["Bleeding control", "Hemostasis", "Easy application"],
      isNew: false,
      isFeatured: false
    },
    {
      id: 4,
      name: "Biliary Stents",
      category: "endoscopy",
      image: "/assets/images/biliary-stents.jpg",
      description: "Biliary stents for SEMS stenting procedures.",
      features: ["Biliary stenting", "SEMS procedures", "Professional grade"],
      isNew: false,
      isFeatured: true
    },
    {
      id: 5,
      name: "AMA RUT Expert",
      category: "endoscopy",
      image: "/assets/images/ama-rut-expert.jpg",
      description: "Professional rapid urease test for H. pylori detection.",
      features: ["H. pylori detection", "Rapid results", "Professional grade"],
      isNew: false,
      isFeatured: true
    },
    {
      id: 6,
      name: "Adjustable Intragastric Balloon",
      category: "endoscopy",
      image: "/assets/images/adjustable-intragastric-balloon.jpg",
      description: "Adjustable intragastric balloon system for weight management procedures.",
      features: ["Adjustable volume", "Weight management", "Minimally invasive"],
      isNew: true,
      isFeatured: true
    }
  ];

  const surgeryProducts = [
    {
      id: 7,
      name: "Disposable Optical Trocar",
      category: "surgery",
      image: "/assets/images/no_image.png",
      description: "Secure, reliable laparoscopic access with optical entry, smooth instrument insertion, and effective sealing performance.",
      features: ["Laparoscopic access", "Optical entry", "SURGAID"],
      isNew: true,
      isFeatured: true,
    },
    {
      id: 8,
      name: "Disposable Bladeless Trocar",
      category: "surgery",
      image: "/assets/images/no_image.png",
      description: "Bladeless trocar for smooth laparoscopic access, reduced tissue trauma, and dependable sealing performance.",
      features: ["Bladeless design", "Laparoscopic access", "SURGAID"],
      isNew: false,
      isFeatured: true,
    },
    {
      id: 9,
      name: "Silicone Round Fluted Drain",
      category: "surgery",
      image: "/assets/images/no_image.png",
      description: "Medical-grade silicone fluted drain for effective closed wound drainage and patient comfort.",
      features: ["Closed wound drainage", "Silicone", "FUSHAN"],
      isNew: false,
      isFeatured: false,
    },
    {
      id: 10,
      name: "Silicone Reservoir",
      category: "surgery",
      image: "/assets/images/no_image.png",
      description: "Closed-circuit silicone reservoir with low vacuum active suction and anti-reflux valve.",
      features: ["Active suction", "Anti-reflux", "FUSHAN"],
      isNew: false,
      isFeatured: false,
    },
    {
      id: 11,
      name: "ADEPTIS Powered Endoscopic Linear Cutting Stapler",
      category: "surgery",
      image: "/assets/images/no_image.png",
      description: "Powered precision stapler with adaptive firing and smart sensing for consistent staple formation.",
      features: ["Powered stapling", "Laparoscopic", "MICONVEY"],
      isNew: false,
      isFeatured: true,
    },
    {
      id: 12,
      name: "Pre-Surgical Scope Warmer",
      category: "surgery",
      image: "/assets/images/no_image.png",
      description: "Sterile disposable anti-fog warming sleeve: ready in minutes, stays warm, clear vision during laparoscopy.",
      features: ["Anti-fog", "Disposable", "PRESURGICAL"],
      isNew: false,
      isFeatured: false,
    },
  ];

  const getCurrentProducts = () => {
    return activeCategory === 'endoscopy' ? endoscopyProducts : surgeryProducts;
  };

  const handleViewAllProducts = () => {
    navigate('/product-portfolio');
  };

  const currentProducts = getCurrentProducts();

  return (
    <section className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary dark:text-white mb-6">
            Our Product Portfolio
          </h2>
          <p className="text-xl text-text-secondary dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Explore our comprehensive range of endoscopic and surgical products, 
            serving medical professionals with GDPMD certified solutions since 2012.
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setActiveCategory(category?.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeCategory === category?.id
                  ? 'bg-primary text-white shadow-medical'
                  : 'bg-muted dark:bg-gray-800 text-text-secondary dark:text-gray-300 hover:bg-primary/10 hover:text-primary'
              }`}
            >
              <Icon name={category?.icon} size={18} />
              <span>{category?.name}</span>
              <span className="text-xs bg-white/20 dark:bg-gray-700/50 px-2 py-1 rounded-full">
                {category?.productCount}
              </span>
            </button>
          ))}
        </motion.div>

        {/* Category Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-lg text-text-secondary dark:text-gray-300 max-w-2xl mx-auto">
            {categories?.find(cat => cat?.id === activeCategory)?.description}
          </p>
        </motion.div>

        {/* Product Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
        >
          {currentProducts?.map((product, index) => (
            <motion.div
              key={product?.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-card dark:bg-gray-800 rounded-xl shadow-medical hover:shadow-medical-hover transition-all duration-300 overflow-hidden group border border-border dark:border-gray-700"
            >
              <div className="relative">
                {getProductImageUrl(product?.image) ? (
                  <Image
                    src={getProductImageUrl(product?.image)}
                    alt={product?.name}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-48 flex items-center justify-center bg-muted text-text-secondary text-sm">No image</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                {product?.isNew && (
                  <div className="absolute top-4 left-4 bg-success text-white px-3 py-1 rounded-full text-sm font-medium">
                    New
                  </div>
                )}
                {product?.isFeatured && (
                  <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                    Featured
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-primary capitalize bg-primary/10 px-3 py-1 rounded-full">
                    {product?.category}
                  </span>
                  <Icon name="Heart" size={18} className="text-text-secondary dark:text-gray-400 hover:text-error cursor-pointer transition-colors duration-200" />
                </div>

                <h3 className="text-lg font-semibold text-text-primary dark:text-white mb-3 leading-tight">{product?.name}</h3>
                <p className="text-text-secondary dark:text-gray-300 text-sm mb-4 leading-relaxed">{product?.description}</p>

                <div className="space-y-2 mb-6">
                  {product?.features?.slice(0, 3)?.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={14} color="var(--color-success)" />
                      <span className="text-xs text-text-secondary dark:text-gray-400">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex space-x-3">
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleViewDetails(product)}
                  >
                    View Details
                  </Button>
                  <Button variant="outline" size="sm" iconName="Download" className="px-3">
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Products CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 border border-primary/10 dark:border-gray-700 mb-8">
            <h3 className="text-2xl font-bold text-text-primary dark:text-white mb-4">
              Explore Our Complete Product Catalog
            </h3>
            <p className="text-text-secondary dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Discover all 67 products across 18 categories, from endoscopic procedures to surgical instruments. 
              Find the perfect medical device for your clinical needs.
            </p>
            <Button 
              variant="default" 
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={handleViewAllProducts}
              className="min-w-48 shadow-glow hover:shadow-glow-hover transition-all duration-300"
            >
              View Complete Product Portfolio
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Product Details Modal */}
      {isModalOpen && selectedProduct && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white dark:bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transition-colors duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-border dark:border-gray-700 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-text-primary dark:text-white">{selectedProduct.name}</h2>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors duration-200"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Product Image */}
                <div className="space-y-4">
                  <div className="relative">
                    {getProductImageUrl(selectedProduct.image) ? (
                      <Image
                        src={getProductImageUrl(selectedProduct.image)}
                        alt={selectedProduct.name}
                        className="w-full h-80 object-cover rounded-xl"
                      />
                    ) : (
                      <div className="w-full h-80 flex items-center justify-center bg-muted text-text-secondary rounded-xl">No image</div>
                    )}
                    {selectedProduct.isNew && (
                      <div className="absolute top-4 left-4 bg-success text-white px-3 py-1 rounded-full text-sm font-medium">
                        New
                      </div>
                    )}
                    {selectedProduct.isFeatured && (
                      <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                        Featured
                      </div>
                    )}
                  </div>
                  
                  {/* Product Badges */}
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm font-medium text-primary capitalize bg-primary/10 px-3 py-1 rounded-full">
                      {selectedProduct.category}
                    </span>
                    {selectedProduct.manufacturer && (
                      <span className="text-sm font-medium text-accent capitalize bg-accent/10 px-3 py-1 rounded-full">
                        {selectedProduct.manufacturer}
                      </span>
                    )}
                    {selectedProduct.model && (
                      <span className="text-sm font-medium text-success capitalize bg-success/10 px-3 py-1 rounded-full">
                        {selectedProduct.model}
                      </span>
                    )}
                  </div>
                </div>

                {/* Product Details */}
                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary dark:text-white mb-3">Description</h3>
                    <p className="text-text-secondary dark:text-gray-300 leading-relaxed">{selectedProduct.description}</p>
                  </div>

                  {/* Features */}
                  {selectedProduct.features && selectedProduct.features.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary dark:text-white mb-3">Key Features</h3>
                      <div className="space-y-2">
                        {selectedProduct.features.map((feature, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                            <span className="text-text-secondary dark:text-gray-400">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Specifications */}
                  {selectedProduct.specifications && (
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary dark:text-white mb-3">Specifications</h3>
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                        {Object.entries(selectedProduct.specifications).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="font-medium text-text-primary dark:text-white capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                            <span className="text-text-secondary dark:text-gray-300">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Certifications */}
                  {selectedProduct.certifications && selectedProduct.certifications.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary dark:text-white mb-3">Certifications</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedProduct.certifications.map((cert, index) => (
                          <span key={index} className="bg-success/10 text-success px-3 py-1 rounded-full text-sm font-medium">
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Availability & Price */}
                  <div className="bg-gradient-to-r from-primary/5 to-accent/5 dark:from-gray-800 dark:to-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg font-semibold text-text-primary dark:text-white">Availability</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedProduct.availability === 'In Stock' 
                          ? 'bg-success/10 text-success' 
                          : 'bg-warning/10 text-warning'
                      }`}>
                        {selectedProduct.availability}
                      </span>
                    </div>
                    {hasDisplayPrice(selectedProduct.price) && (
                      <div className="text-2xl font-bold text-primary">
                        {selectedProduct.price}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex space-x-4 mt-8 pt-6 border-t border-border dark:border-gray-700">
                <Button variant="default" size="lg" className="flex-1">
                  <Icon name="Download" size={18} className="mr-2" />
                  Download Brochure
                </Button>
                <Button variant="outline" size="lg" className="flex-1">
                  <Icon name="Mail" size={18} className="mr-2" />
                  Request Quote
                </Button>
                <Button variant="outline" size="lg">
                  <Icon name="Heart" size={18} />
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default ProductPortfolioSection;
